import React from 'react';
import { FormattedMessage } from 'react-intl';
import { formatMinorUnits, formatPercent, formatQuantity } from '../reportFormatters';
import { ReportMetricCard } from './ReportMetricCard';
import { SummaryCharts } from './SummaryCharts';

function comparisonValue(changes, key) {
  const value = changes && changes[key];
  return value === null || value === undefined ? null : formatPercent(value);
}

function formatMoneyDelta(value, money) {
  return formatMinorUnits({
    currency: (money && money.currency) || 'RON',
    without_vat_minor: value
  });
}

function formatPercentagePoints(value) {
  const formatted = formatPercent(value);
  return formatted === '—' ? formatted : formatted.replace('%', ' pp');
}

function PreviousLabel({ comparisonMode }) {
  return (
    <FormattedMessage
      id={comparisonMode === 'previous_calendar_year'
        ? 'components.pages.reports.filters.previousCalendarYear'
        : 'components.pages.reports.summary.comparisonSeries'}
    />
  );
}

function SummaryReport({
  chartSeriesScope,
  comparisonMode,
  data,
  error,
  financialComparisonEnabled,
  loading,
  onFinancialComparisonChange,
  onProductComparisonChange,
  onRetry,
  periodPreset,
  productComparisonEnabled
}) {
  const result = data || {};
  const metrics = result.metrics || {};
  const comparison = result.comparison || null;
  const previousMetrics = (comparison && (comparison.metrics || comparison.previous_metrics)) || {};
  const changes = comparison && comparison.changes ? comparison.changes : {};
  const orders = metrics.orders || {};
  const previousOrders = previousMetrics.orders || {};
  const activity = result.activity || {};
  const signedPackages = (activity.signed_packages && activity.signed_packages.total) || {};

  if (loading) {
    return (
      <div aria-busy="true" className="report-summary-loading" data-testid="reports-loading">
        {[1, 2, 3, 4].map(item => <span key={item}/>) }
      </div>
    );
  }

  if (error) {
    return (
      <div className="report-state report-state--error" role="alert">
        <i aria-hidden="true" className="fas fa-exclamation-circle"/>
        <FormattedMessage id="components.pages.reports.summary.error"/>
        <button className="btn btn-outline-primary btn-sm" onClick={onRetry} type="button">
          <FormattedMessage id="components.pages.reports.summary.retry"/>
        </button>
      </div>
    );
  }

  if (!data || !result.metrics) {
    return <div className="report-state"><FormattedMessage id="components.pages.reports.summary.empty"/></div>;
  }

  return (
    <div className="report-summary">
      <section aria-label="Macro KPI" className="report-macro-kpis">
        <ReportMetricCard
          labelId="components.pages.reports.summary.commercialValue"
          primary={formatMinorUnits(metrics.commercial_value || metrics.net_sales)}
          testId="metric-net-sales"
        >
          <span><FormattedMessage id="components.pages.reports.money.paidNet"/>: {formatMinorUnits(metrics.net_sales)}</span>
          <span><FormattedMessage id="components.pages.reports.money.directAllocation"/>: {formatMinorUnits(metrics.direct_allocation_value)}</span>
          <span><FormattedMessage id="components.pages.reports.money.commercial"/>: {formatMinorUnits(metrics.commercial_value || metrics.net_sales)}</span>
          <span><FormattedMessage id="components.pages.reports.money.paidNet"/> (<PreviousLabel comparisonMode={comparisonMode}/>): {formatMinorUnits(previousMetrics.net_sales)} · Δ: {formatMoneyDelta(changes.net_sales_without_vat_minor_delta, metrics.net_sales)}</span>
        </ReportMetricCard>
        <ReportMetricCard
          comparison={comparisonValue(changes, 'orders_percent')}
          labelId="components.pages.reports.summary.orders"
          primary={formatQuantity(orders.total)}
          testId="metric-orders"
        >
          <span><FormattedMessage id="components.pages.reports.summary.total"/>: {formatQuantity(orders.total)} · <PreviousLabel comparisonMode={comparisonMode}/>: {formatQuantity(previousOrders.total)} · Δ: {formatQuantity(changes.orders_total_delta)}</span>
          <span><FormattedMessage id="components.pages.reports.summary.billable"/>: {formatQuantity(orders.billable)} · <PreviousLabel comparisonMode={comparisonMode}/>: {formatQuantity(previousOrders.billable)} · Δ: {formatQuantity(changes.orders_billable_delta)}</span>
          <span><FormattedMessage id="components.pages.reports.summary.directAllocation"/>: {formatQuantity(orders.direct_allocation)} · <PreviousLabel comparisonMode={comparisonMode}/>: {formatQuantity(previousOrders.direct_allocation)} · Δ: {formatQuantity(changes.orders_direct_allocation_delta)}</span>
        </ReportMetricCard>
        <ReportMetricCard
          comparison={comparisonValue(changes, 'active_organizations_percent')}
          labelId="components.pages.reports.summary.activeOrganizations"
          primary={formatQuantity(metrics.active_organizations)}
          testId="metric-active-organizations"
        >
          <span><PreviousLabel comparisonMode={comparisonMode}/>: {formatQuantity(previousMetrics.active_organizations)} · Δ: {formatQuantity(changes.active_organizations_delta)}</span>
          <span><FormattedMessage id="components.pages.reports.summary.signedPackages"/>: {formatQuantity(signedPackages.current)} · <PreviousLabel comparisonMode={comparisonMode}/>: {formatQuantity(signedPackages.previous)} · Δ: {formatQuantity(signedPackages.delta)}</span>
        </ReportMetricCard>
        <ReportMetricCard
          comparison={comparisonValue(changes, 'refund_rate_percent')}
          labelId="components.pages.reports.summary.refundRate"
          primary={formatPercent(metrics.refund_rate)}
          testId="metric-refunds"
        >
          <span><FormattedMessage id="components.pages.reports.summary.refundValue"/>: {formatMinorUnits(metrics.refunds)}</span>
          <span><FormattedMessage id="components.pages.reports.summary.refundRate"/> (<PreviousLabel comparisonMode={comparisonMode}/>): {formatPercent(previousMetrics.refund_rate)} · Δ: {formatPercentagePoints(changes.refund_rate_percentage_point_delta)}</span>
          <span><FormattedMessage id="components.pages.reports.summary.refundValue"/> (<PreviousLabel comparisonMode={comparisonMode}/>): {formatMinorUnits(previousMetrics.refunds)} · Δ: {formatMoneyDelta(changes.refunds_without_vat_minor_delta, metrics.refunds)}</span>
        </ReportMetricCard>
      </section>

      <SummaryCharts
        chartSeriesScope={chartSeriesScope}
        financialComparisonEnabled={financialComparisonEnabled}
        onFinancialComparisonChange={onFinancialComparisonChange}
        onProductComparisonChange={onProductComparisonChange}
        comparisonPeriod={result.meta && result.meta.comparison_period}
        comparisonSeries={comparison && comparison.series}
        currentPeriod={result.meta && result.meta.period}
        dateTo={result.meta && result.meta.period && result.meta.period.date_to}
        periodPreset={periodPreset}
        productComparisonEnabled={productComparisonEnabled}
        productSeries={result.product_series}
        series={result.series}
      />
    </div>
  );
}

export { SummaryReport };
